import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client.js';
import { formatBrl } from '../../utils/currency.js';
import { orderStatusLabelPtBr } from '../../utils/orderStatus.js';
import { AdminSaasFinancePanel } from './AdminSaasFinancePanel.jsx';

const STATUS_OPTS = ['pending', 'paid', 'sent_to_erp', 'cancelled'];

function localYmd(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** @param {string} ymd */
function shiftYmd(ymd, deltaDays) {
  const [y, mo, dd] = ymd.split('-').map(Number);
  const dt = new Date(y, mo - 1, dd + deltaDays);
  return localYmd(dt);
}

/** @param {unknown} ymd */
function formatYmdPtBr(ymd) {
  const s = typeof ymd === 'string' ? ymd.trim() : '';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s || '—';
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function FinanceOrderItemsTable({ items }) {
  if (items == null) return <p className="text-xs text-ink/55 dark:text-frost-muted">Sem linhas de artigos.</p>;
  if (!Array.isArray(items)) {
    return (
      <pre className="max-h-48 overflow-auto rounded-xl border border-ink/10 bg-black/[0.03] p-3 font-mono text-[10px] dark:border-white/10 dark:bg-white/[0.04]">
        {JSON.stringify(items, null, 2)}
      </pre>
    );
  }
  if (items.length === 0) return <p className="text-xs text-ink/55 dark:text-frost-muted">Carrinho vazio no registo.</p>;
  return (
    <div className="overflow-x-auto rounded-xl border border-ink/10 dark:border-white/10">
      <table className="w-full min-w-[320px] border-collapse text-left text-xs">
        <thead>
          <tr className="border-b border-ink/10 bg-zinc-100/90 text-[10px] font-bold uppercase text-ink dark:border-white/10 dark:bg-zinc-900/90 dark:text-mist">
            <th className="px-3 py-2">Artigo</th>
            <th className="px-3 py-2 text-right">Qtd</th>
            <th className="px-3 py-2 text-right">Preço</th>
            <th className="px-3 py-2 text-right">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => {
            const pid = it?.product_id ?? it?.id;
            const nome = it?.nome ?? (pid != null ? `Produto #${pid}` : `Linha ${idx + 1}`);
            const qty = Number(it?.qty ?? it?.quantity ?? 0) || 0;
            const preco = Number(it?.preco ?? it?.unit ?? 0);
            const sub = Number.isFinite(preco) && qty ? preco * qty : Number(it?.line ?? 0);
            return (
              <tr key={idx} className="border-b border-ink/5 dark:border-white/5">
                <td className="px-3 py-2">
                  <div className="font-medium text-ink dark:text-mist">{nome}</div>
                  {pid != null ? (
                    <div className="font-mono text-[10px] text-ink/55 dark:text-frost-muted">#{pid}</div>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{qty || '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{Number.isFinite(preco) ? formatBrl(preco) : '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatBrl(sub)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LedgerKpi({ label, value, detail, tint = 'emerald' }) {
  const t =
    tint === 'amber'
      ? 'from-amber-400/30 to-transparent border-amber-500/35'
      : tint === 'slate'
        ? 'from-slate-400/25 to-transparent border-slate-500/35'
        : 'from-emerald-400/30 to-transparent border-emerald-500/40';
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br p-5 ${t} backdrop-blur-sm dark:bg-white/[0.03]`}
    >
      <div className="pointer-events-none absolute right-0 top-0 h-20 w-20 -translate-y-6 translate-x-6 rounded-full bg-white/40 blur-2xl dark:bg-emerald-500/15" />
      <p className="relative text-[10px] font-bold uppercase tracking-[0.22em] text-ink/50 dark:text-frost-muted">{label}</p>
      <p className="relative mt-3 font-display text-2xl font-semibold tracking-tight text-ink tabular-nums dark:text-frost">
        {value}
      </p>
      {detail ? (
        <p className="relative mt-2 text-[11px] font-medium leading-relaxed text-ink/55 dark:text-frost-muted">{detail}</p>
      ) : null}
    </div>
  );
}

function MiniBars({ days, byDayMap }) {
  const maxRev = Math.max(1, ...days.map((d) => Number(byDayMap[d]?.revenue || 0)));
  return (
    <div className="flex h-36 items-end gap-1 px-1">
      {days.map((d) => {
        const cell = byDayMap[d] || { revenue: 0, cnt: 0 };
        const hPct = Math.round((Number(cell.revenue) / maxRev) * 100);
        const short = `${d.slice(8, 10)}/${d.slice(5, 7)}`;
        return (
          <div
            key={d}
            title={`${d}: ${formatBrl(cell.revenue)} · ${cell.cnt} pedidos`}
            className="flex min-w-0 flex-1 flex-col items-center gap-1"
          >
            <div className="flex w-full flex-1 items-end justify-center">
              <div
                className="w-full max-w-[12px] rounded-t bg-gradient-to-t from-emerald-600/95 to-accent/85 opacity-95 shadow-sm dark:from-emerald-400/80 dark:to-accent/75"
                style={{ height: `${Math.max(6, hPct)}%` }}
              />
            </div>
            <span className="tabular-nums text-[8px] font-bold uppercase text-ink/50 dark:text-frost-muted">{short}</span>
          </div>
        );
      })}
    </div>
  );
}

function csvEscape(s) {
  const v = String(s ?? '');
  if (/[,"\n\r]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

export function AdminFinanceSection() {
  /** @type{['pedidos' | 'saas', function]} */
  const [finMainTab, setFinMainTab] = useState('pedidos');
  const [busy, setBusy] = useState(true);
  const [msg, setMsg] = useState('');
  const [summary, setSummary] = useState(null);
  /** @type{'7' | '30' | 'range' | 'all'} */
  const [periodPreset, setPeriodPreset] = useState('30');
  const [rangeFrom, setRangeFrom] = useState(() => shiftYmd(localYmd(), -29));
  const [rangeTo, setRangeTo] = useState(() => localYmd());

  const [tenants, setTenants] = useState([]);
  const [tenantFilterOrders, setTenantFilterOrders] = useState('');
  const [statusFilterOrders, setStatusFilterOrders] = useState('');
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [orderOffset, setOrderOffset] = useState(0);
  const orderLimit = 40;

  const [coupons, setCoupons] = useState([]);
  const [couponTenant, setCouponTenant] = useState('');
  const [couponUnusedOnly, setCouponUnusedOnly] = useState(false);
  const [newCupTenantId, setNewCupTenantId] = useState('');
  const [newCupCode, setNewCupCode] = useState('');
  const [newCupPct, setNewCupPct] = useState('10');
  /** @type {'single'|'multi'} */
  const [newCupKind, setNewCupKind] = useState('single');
  const [newCupValidUntil, setNewCupValidUntil] = useState('');
  const [newCupMaxUses, setNewCupMaxUses] = useState('');
  const [cupBusy, setCupBusy] = useState(false);

  /** @type{null | { order: Record<string, unknown> }} */
  const [statusModal, setStatusModal] = useState(null);
  const [statusPick, setStatusPick] = useState('paid');
  const [statusSaving, setStatusSaving] = useState(false);

  /** Painel lateral: detalhe completo do pedido (GET /admin/finance/order). */
  const [orderDrawer, setOrderDrawer] = useState(null);
  const [orderDrawerData, setOrderDrawerData] = useState(null);
  const [orderDrawerBusy, setOrderDrawerBusy] = useState(false);
  const [orderDrawerErr, setOrderDrawerErr] = useState('');

  const summaryQs = useMemo(() => {
    if (periodPreset === 'all') return '?period=all';
    let from = rangeFrom;
    let to = rangeTo;
    if (periodPreset === '7') {
      to = localYmd();
      from = shiftYmd(to, -6);
    } else if (periodPreset === '30') {
      to = localYmd();
      from = shiftYmd(to, -29);
    }
    return `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
  }, [periodPreset, rangeFrom, rangeTo]);

  const ordersQs = useMemo(() => {
    const p = new URLSearchParams();
    if (periodPreset !== 'all') {
      let from = rangeFrom;
      let to = rangeTo;
      if (periodPreset === '7') {
        to = localYmd();
        from = shiftYmd(to, -6);
      } else if (periodPreset === '30') {
        to = localYmd();
        from = shiftYmd(to, -29);
      }
      p.set('from', from);
      p.set('to', to);
    } else {
      p.set('period', 'all');
    }
    const tid = Number(tenantFilterOrders);
    if (Number.isFinite(tid) && tid > 0) p.set('tenant_id', String(tid));
    if (statusFilterOrders) p.set('status', statusFilterOrders);
    p.set('limit', String(orderLimit));
    p.set('offset', String(orderOffset));
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [periodPreset, rangeFrom, rangeTo, tenantFilterOrders, statusFilterOrders, orderOffset]);

  const loadSummary = useCallback(async () => {
    try {
      const d = await api(`/admin/finance/summary${summaryQs}`);
      setSummary(d);
    } catch (e) {
      setSummary(null);
      setMsg(e.message || 'Erro ao carregar resumo financeiro.');
    }
  }, [summaryQs]);

  const loadOrders = useCallback(async () => {
    try {
      const d = await api(`/admin/finance/orders${ordersQs}`);
      setOrders(d.orders || []);
      setOrdersTotal(Number(d.total || 0));
    } catch (e) {
      setOrders([]);
      setMsg(e.message || 'Erro ao carregar pedidos.');
    }
  }, [ordersQs]);

  const loadCoupons = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      const tid = Number(couponTenant);
      if (Number.isFinite(tid) && tid > 0) p.set('tenant_id', String(tid));
      if (couponUnusedOnly) p.set('unused_only', '1');
      const qs = p.toString() ? `?${p.toString()}` : '';
      const d = await api(`/admin/finance/coupons${qs}`);
      setCoupons(d.coupons || []);
    } catch (e) {
      setCoupons([]);
      setMsg(e.message || 'Erro ao carregar cupons.');
    }
  }, [couponTenant, couponUnusedOnly]);

  useEffect(() => {
    (async () => {
      try {
        const dt = await api('/admin/tenants');
        setTenants(dt.tenants || []);
      } catch {
        setTenants([]);
      }
    })();
  }, []);

  useEffect(() => {
    setMsg('');
    setBusy(true);
    (async () => {
      await loadSummary();
      setBusy(false);
    })().catch(() => setBusy(false));
  }, [loadSummary]);

  useEffect(() => {
    loadOrders().catch(() => {});
  }, [loadOrders]);

  useEffect(() => {
    loadCoupons().catch(() => {});
  }, [loadCoupons]);

  useEffect(() => {
    if (!orderDrawer) {
      setOrderDrawerData(null);
      setOrderDrawerErr('');
      setOrderDrawerBusy(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setOrderDrawerBusy(true);
      setOrderDrawerErr('');
      setOrderDrawerData(null);
      try {
        const qs = new URLSearchParams({
          tenant_id: String(orderDrawer.tenant_id),
          order_id: String(orderDrawer.order_id),
        });
        const d = await api(`/admin/finance/order?${qs.toString()}`);
        if (!cancelled) setOrderDrawerData(d.order || null);
      } catch (e) {
        if (!cancelled) {
          setOrderDrawerErr(e.message || 'Erro ao carregar o pedido.');
          setOrderDrawerData(null);
        }
      } finally {
        if (!cancelled) setOrderDrawerBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderDrawer]);

  useEffect(() => {
    if (!orderDrawer) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') setOrderDrawer(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [orderDrawer]);

  const byDayMap = useMemo(() => {
    const m = {};
    if (!summary?.by_day) return m;
    for (const row of summary.by_day) {
      const key = typeof row.d === 'string' ? row.d.slice(0, 10) : localYmd(new Date(row.d));
      m[key] = { revenue: Number(row.revenue || 0), cnt: Number(row.cnt || 0) };
    }
    return m;
  }, [summary]);

  const dayKeysSorted = useMemo(() => Object.keys(byDayMap).sort(), [byDayMap]);

  const glob = summary?.global || {};

  async function refreshAll() {
    setMsg('');
    await loadSummary();
    await loadOrders();
    await loadCoupons();
  }

  function exportCsv() {
    const head = [
      'id',
      'tenant_id',
      'empresa',
      'status',
      'total',
      'subtotal_antes_desconto',
      'percent_desconto',
      'cupom',
      'mp_payment_id',
      'mp_payment_status',
      'cliente',
      'email',
      'criado_em',
    ];
    const lines = [head.join(',')];
    for (const o of orders) {
      lines.push(
        [
          o.id,
          o.tenant_id,
          csvEscape(o.tenant_nome),
          o.status,
          o.total,
          o.subtotal_before_discount ?? '',
          o.discount_percent ?? '',
          csvEscape(o.coupon_code ?? ''),
          csvEscape(o.mp_payment_id ?? ''),
          csvEscape(o.mp_payment_status ?? ''),
          csvEscape(o.customer_nome),
          csvEscape(o.email),
          csvEscape(o.created_at),
        ].join(',')
      );
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `ominia-fin-pedidos-${localYmd()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  async function submitCoupon(e) {
    e.preventDefault();
    const tid = Number(newCupTenantId);
    if (!Number.isFinite(tid) || tid < 1) {
      setMsg('Escolha a empresa para o cupom.');
      return;
    }
    const kind = newCupKind === 'multi' ? 'multi' : 'single';
    /** @type{Record<string, unknown>} */
    const body = {
      code: newCupCode.trim(),
      percent_discount: Number(newCupPct),
      kind,
    };
    if (kind === 'multi') {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(newCupValidUntil.trim())) {
        setMsg('Campanha: indique valid_until em AAAA-MM-DD.');
        return;
      }
      body.valid_until = newCupValidUntil.trim();
      const mu = String(newCupMaxUses).trim();
      if (mu !== '') {
        const n = Math.floor(Number(mu.replace(/\s/g, '')));
        if (!(n >= 1 && n <= 100000000)) {
          setMsg('Limite de usos: inteiro entre 1 e 100000000 ou vazio.');
          return;
        }
        body.max_uses = n;
      }
    }
    setCupBusy(true);
    setMsg('');
    try {
      await api(`/tenants/${tid}/discount-coupons`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setNewCupCode('');
      setNewCupKind('single');
      setNewCupValidUntil('');
      setNewCupMaxUses('');
      await loadCoupons();
      await loadSummary();
    } catch (err) {
      setMsg(err.message || 'Falha ao criar cupom.');
    } finally {
      setCupBusy(false);
    }
  }

  async function removeCoupon(cid, tenantId, labelHint = '') {
    if (!window.confirm(`Remover este cupom${labelHint}? Só permitido quando ainda aplicável aos critérios do servidor.`)) return;
    setCupBusy(true);
    try {
      await api(`/tenants/${tenantId}/discount-coupons/${cid}`, { method: 'DELETE' });
      await loadCoupons();
      await loadSummary();
    } catch (err) {
      setMsg(err.message || 'Não foi possível remover.');
    } finally {
      setCupBusy(false);
    }
  }

  async function saveOrderStatus() {
    if (!statusModal?.order) return;
    const o = /** @type{Record<string,any>} */ (statusModal.order);
    setStatusSaving(true);
    setMsg('');
    try {
      await api('/admin/finance/order-status', {
        method: 'PATCH',
        body: JSON.stringify({
          tenant_id: o.tenant_id,
          order_id: o.id,
          status: statusPick,
        }),
      });
      setStatusModal(null);
      await loadOrders();
      await loadSummary();
    } catch (err) {
      setMsg(err.message || 'Não foi possível atualizar o status.');
    } finally {
      setStatusSaving(false);
    }
  }

  function scrollFinanceSection(elementId) {
    if (typeof document === 'undefined') return;
    window.requestAnimationFrame(() => {
      document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function openStatusFromOrderDrawer() {
    const od = orderDrawerData;
    if (!od) return;
    const cur = typeof od.status === 'string' ? od.status.trim() : '';
    setStatusPick(STATUS_OPTS.includes(cur) ? cur : 'pending');
    setStatusModal({
      order: {
        id: od.id,
        tenant_id: od.tenant_id,
        tenant_nome: od.tenant_nome,
        status: od.status,
        total: od.total,
        subtotal_before_discount: od.subtotal_before_discount,
        discount_percent: od.discount_percent,
        coupon_code: od.coupon_code,
        mp_preference_id: od.mp_preference_id,
        mp_payment_id: od.mp_payment_id,
        mp_payment_status: od.mp_payment_status,
        customer_nome: od.customer_nome,
        email: od.customer_email,
        telefone: od.customer_telefone,
        created_at: od.created_at,
        updated_at: od.updated_at,
      },
    });
    setOrderDrawer(null);
  }

  return (
    <div className="space-y-10">
      <section className="rounded-3xl border border-ink/10 bg-gradient-to-br from-slate-500/[0.08] via-white/90 to-emerald-500/[0.09] px-6 py-6 shadow-sm dark:border-white/10 dark:from-slate-500/10 dark:via-[#0d1512] dark:to-emerald-950/25">
        <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-ink/50 dark:text-frost-muted">Admin global</p>
        <h2 className="mt-1 font-display text-2xl font-bold tracking-tight text-ink dark:text-mist">Módulo financeiro</h2>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink/70 dark:text-frost-muted">
          Visão unificada: <strong className="text-ink dark:text-mist">pedidos e receita</strong> (Mercado Pago, estados,
          cupons, exportação) e <strong className="text-ink dark:text-mist">assinaturas SaaS</strong> (cobranças, atrasos,
          edição de valores em aberto). Use os atalhos para saltar dentro do separador ou mudar de área.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              setFinMainTab('pedidos');
              setTimeout(() => scrollFinanceSection('fin-mod-overview'), 50);
            }}
            className="rounded-full border border-ink/15 bg-white/90 px-4 py-2 text-xs font-bold uppercase tracking-wide text-ink shadow-sm dark:border-white/15 dark:bg-[#121c18] dark:text-mist"
          >
            Pedidos · período &amp; resumo
          </button>
          <button
            type="button"
            onClick={() => {
              setFinMainTab('pedidos');
              setTimeout(() => scrollFinanceSection('fin-mod-orders'), 50);
            }}
            className="rounded-full border border-ink/15 bg-white/90 px-4 py-2 text-xs font-bold uppercase tracking-wide text-ink shadow-sm dark:border-white/15 dark:bg-[#121c18] dark:text-mist"
          >
            Pedidos · listagem
          </button>
          <button
            type="button"
            onClick={() => {
              setFinMainTab('pedidos');
              setTimeout(() => scrollFinanceSection('fin-mod-coupons'), 50);
            }}
            className="rounded-full border border-ink/15 bg-white/90 px-4 py-2 text-xs font-bold uppercase tracking-wide text-ink shadow-sm dark:border-white/15 dark:bg-[#121c18] dark:text-mist"
          >
            Cupons
          </button>
          <button
            type="button"
            onClick={() => {
              setFinMainTab('saas');
              setTimeout(() => scrollFinanceSection('fin-saas-head'), 50);
            }}
            className="rounded-full border border-violet-500/35 bg-violet-500/15 px-4 py-2 text-xs font-bold uppercase tracking-wide text-violet-950 dark:bg-violet-500/20 dark:text-violet-50"
          >
            Assinaturas SaaS
          </button>
        </div>
      </section>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setFinMainTab('pedidos')}
          className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wide ${
            finMainTab === 'pedidos'
              ? 'bg-ink text-mist dark:bg-mist dark:text-ink'
              : 'border border-ink/15 bg-white/70 text-ink/65 dark:border-white/15 dark:bg-white/[0.08] dark:text-frost'
          }`}
        >
          Pedidos &amp; canal de receita
        </button>
        <button
          type="button"
          onClick={() => setFinMainTab('saas')}
          className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wide ${
            finMainTab === 'saas'
              ? 'bg-violet-700 text-white dark:bg-violet-500 dark:text-white'
              : 'border border-violet-500/35 bg-white/70 text-violet-900 dark:border-violet-500/30 dark:bg-violet-950/35 dark:text-violet-100'
          }`}
        >
          Assinaturas SaaS (por loja · por cliente)
        </button>
      </div>

      {finMainTab === 'saas' ? (
        <div id="fin-saas-head" className="scroll-mt-24">
          <AdminSaasFinancePanel />
        </div>
      ) : null}

      {finMainTab === 'pedidos' ? (
        <>
          <div
            id="fin-mod-overview"
            className="scroll-mt-24 relative overflow-hidden rounded-[2rem] border border-ink/10 bg-gradient-to-br from-[#ecfdf5]/90 via-white/90 to-[#f0fdf4]/80 px-8 py-8 shadow-night-card backdrop-blur dark:border-emerald-500/20 dark:from-emerald-950/40 dark:via-[#0b1412] dark:to-[#081018] dark:shadow-none"
          >
        <div className="pointer-events-none absolute -left-20 top-10 h-64 w-64 rounded-full bg-emerald-400/15 blur-[100px]" />
        <div className="pointer-events-none absolute -right-10 bottom-0 h-48 w-48 rounded-full bg-teal-500/20 blur-[88px]" />
        <div className="relative flex flex-wrap items-start justify-between gap-6">
          <div className="max-w-xl space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.38em] text-emerald-800/80 dark:text-emerald-200/85">
              Tesouraria SaaS · visão única
            </p>
            <h3 className="font-display text-2xl font-semibold tracking-tight text-ink dark:text-frost">
              Centro financeiro global
            </h3>
            <p className="text-sm leading-relaxed text-ink/65 dark:text-frost-muted">
              Concilia GMV confirmado (Mercado Pago e operação), canal de valores pendentes, impacto dos cupons e rankings
              por loja — tudo só no perfil corporativo da plataforma.
            </p>
          </div>
          <button
            type="button"
            onClick={() => refreshAll()}
            disabled={busy}
            className="rounded-2xl border border-emerald-600/35 bg-emerald-600/[0.12] px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-emerald-900 backdrop-blur transition hover:bg-emerald-600/25 dark:border-emerald-400/35 dark:bg-emerald-500/15 dark:text-emerald-50"
          >
            Atualizar dados
          </button>
        </div>

        <div className="relative mt-6 flex flex-wrap items-center gap-2">
          {[
            ['7', '7 dias'],
            ['30', '30 dias'],
            ['range', 'Datas'],
            ['all', 'Histórico completo'],
          ].map(([id, lbl]) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                setPeriodPreset(/** @type{any} */ (id));
                setOrderOffset(0);
              }}
              className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wide transition ${
                periodPreset === id
                  ? 'bg-ink text-mist shadow dark:bg-mist dark:text-ink'
                  : 'bg-black/5 text-ink/60 hover:bg-black/10 dark:bg-white/[0.1] dark:text-frost'
              }`}
            >
              {lbl}
            </button>
          ))}
        </div>

        {periodPreset === 'range' && (
          <div className="relative mt-4 flex flex-wrap items-end gap-3">
            <label className="text-[11px] font-bold uppercase tracking-wide text-ink/55 dark:text-frost-muted">
              De
              <input
                type="date"
                value={rangeFrom}
                onChange={(e) => setRangeFrom(e.target.value)}
                className="mt-1 block rounded-xl border border-ink/15 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#0f1815]"
              />
            </label>
            <label className="text-[11px] font-bold uppercase tracking-wide text-ink/55 dark:text-frost-muted">
              Até
              <input
                type="date"
                value={rangeTo}
                onChange={(e) => setRangeTo(e.target.value)}
                className="mt-1 block rounded-xl border border-ink/15 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#0f1815]"
              />
            </label>
          </div>
        )}
      </div>

      <nav
        aria-label="Secções do módulo financeiro (pedidos)"
        className="sticky top-2 z-20 flex flex-wrap gap-1.5 rounded-2xl border border-ink/10 bg-white/90 p-1.5 shadow-sm backdrop-blur-md dark:border-white/10 dark:bg-[#0f1815]/95"
      >
        {[
          ['fin-mod-overview', 'Resumo'],
          ['fin-mod-indicators', 'Indicadores'],
          ['fin-mod-ranking', 'Ranking'],
          ['fin-mod-orders', 'Pedidos'],
          ['fin-mod-coupons', 'Cupons'],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => scrollFinanceSection(id)}
            className="rounded-xl px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-ink/75 transition hover:bg-ink/10 hover:text-ink dark:text-frost dark:hover:bg-white/10 dark:hover:text-mist"
          >
            {label}
          </button>
        ))}
      </nav>

      {msg ? (
        <div className="rounded-2xl border border-amber-500/45 bg-amber-500/10 px-4 py-3 text-sm font-semibold text-amber-950 dark:text-amber-100">
          {msg}
        </div>
      ) : null}

      {busy && !summary ? (
        <p className="text-sm text-ink/55 dark:text-frost-muted">A carregar indicadores…</p>
      ) : (
        <div id="fin-mod-indicators" className="scroll-mt-28 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <LedgerKpi
            label="Receita confirmada"
            value={formatBrl(glob.revenue_confirmed)}
            detail={`${glob.count_confirmed ?? 0} pedidos pagos / enviados ao ERP no período`}
          />
          <LedgerKpi
            label="GMV bruto (todos estados)"
            value={formatBrl(glob.gmv_all)}
            detail={`${glob.orders_total ?? 0} pedidos — inclui pendentes e cancelados`}
            tint="slate"
          />
          <LedgerKpi
            label="Pipeline pendente"
            value={formatBrl(glob.pipeline_pending)}
            detail={`${glob.count_pending ?? 0} em aberto · checkout MP em curso: ${glob.checkout_pending_prefs ?? 0}`}
            tint="amber"
          />
          <LedgerKpi
            label="Descontos (cupom)"
            value={formatBrl(glob.discount_value_estimated)}
            detail={`${glob.orders_with_coupon ?? 0} pedidos com cupom · Ref. MP: ${glob.orders_mp_with_payment_ref ?? 0}`}
          />
        </div>
      )}

      {summary?.coupon_stats ? (
        <div className="flex flex-wrap gap-3 rounded-2xl border border-ink/10 bg-white/60 px-5 py-4 text-sm dark:border-white/10 dark:bg-white/[0.04]">
          <span className="font-bold text-ink dark:text-mist">Cupons na plataforma</span>
          <span className="tabular-nums text-ink/75 dark:text-frost">
            total {summary.coupon_stats.total ?? 0}
          </span>
          <span className="rounded-full bg-emerald-500/15 px-3 py-0.5 text-xs font-semibold text-emerald-900 dark:bg-emerald-500/25 dark:text-emerald-50">
            aplicáveis agora {summary.coupon_stats.unused ?? 0}
          </span>
          <span className="rounded-full bg-slate-500/15 px-3 py-0.5 text-xs font-semibold text-slate-800 dark:bg-slate-400/30 dark:text-slate-100">
            uso único consumidos {summary.coupon_stats.used ?? 0}
          </span>
        </div>
      ) : null}

      <div id="fin-mod-ranking" className="scroll-mt-24 grid gap-8 lg:grid-cols-2">
        <div className="space-y-3 rounded-3xl border border-ink/10 bg-white/70 p-6 dark:border-white/10 dark:bg-white/[0.04]">
          <div className="flex items-center justify-between gap-2">
            <h4 className="font-display text-lg font-semibold text-ink dark:text-mist">Receita diária (confirmada)</h4>
            <span className="text-[10px] font-bold uppercase tracking-wider text-ink/50 dark:text-frost-muted">
              {periodPreset === 'all' ? '— use intervalo —' : `${dayKeysSorted.length} dias`}
            </span>
          </div>
          {dayKeysSorted.length === 0 ? (
            <p className="text-sm text-ink/58 dark:text-frost-muted">Sem dados no período escolhido ou no histórico completo.</p>
          ) : (
            <MiniBars days={dayKeysSorted} byDayMap={byDayMap} />
          )}
        </div>

        <div className="space-y-3 rounded-3xl border border-ink/10 bg-white/70 p-6 dark:border-white/10 dark:bg-white/[0.04]">
          <h4 className="font-display text-lg font-semibold text-ink dark:text-mist">Cancelamentos &amp; saídas</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-2xl border border-ink/10 bg-black/[0.03] p-4 dark:border-white/10 dark:bg-white/[0.04]">
              <p className="text-[10px] font-bold uppercase text-ink/50 dark:text-frost-muted">Pedidos cancelados</p>
              <p className="mt-2 font-display text-xl font-semibold tabular-nums text-ink dark:text-mist">{glob.count_cancelled ?? 0}</p>
            </div>
            <div className="rounded-2xl border border-ink/10 bg-black/[0.03] p-4 dark:border-white/10 dark:bg-white/[0.04]">
              <p className="text-[10px] font-bold uppercase text-ink/50 dark:text-frost-muted">Valor total</p>
              <p className="mt-2 font-display text-xl font-semibold tabular-nums text-amber-800 dark:text-amber-200">
                {formatBrl(glob.value_cancelled)}
              </p>
            </div>
          </div>
          <p className="text-[11px] leading-relaxed text-ink/50 dark:text-frost-muted">
            Valor cancelado reflete o total gravado dos pedidos com estado cancelado (contexto operacional, não liquidação
            bancária).
          </p>
        </div>
      </div>

      <div className="rounded-3xl border border-ink/10 bg-white/70 p-6 dark:border-white/10 dark:bg-white/[0.04]">
        <h4 className="font-display text-lg font-semibold text-ink dark:text-mist">Ranking por empresa</h4>
        <p className="mt-1 text-xs text-ink/50 dark:text-frost-muted">
          Clique numa linha para aplicar o filtro de empresa na listagem de pedidos abaixo.
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-ink/15 bg-zinc-100/95 text-[11px] font-bold uppercase tracking-wide text-ink dark:border-white/15 dark:bg-zinc-900/85 dark:text-mist">
                <th className="px-3 py-2.5 pr-4">Empresa</th>
                <th className="px-3 py-2.5 pr-4">Slug</th>
                <th className="px-3 py-2.5 pr-4 text-right">Pedidos</th>
                <th className="px-3 py-2.5 pr-4 text-right">GMV</th>
                <th className="px-3 py-2.5 text-right">Receita OK</th>
              </tr>
            </thead>
            <tbody>
              {(summary?.by_tenant || []).map((t) => (
                <tr
                  key={t.tenant_id}
                  role="button"
                  tabIndex={0}
                  title="Filtrar pedidos desta empresa"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setTenantFilterOrders(String(t.tenant_id));
                      setOrderOffset(0);
                      scrollFinanceSection('fin-mod-orders');
                    }
                  }}
                  onClick={() => {
                    setTenantFilterOrders(String(t.tenant_id));
                    setOrderOffset(0);
                    scrollFinanceSection('fin-mod-orders');
                  }}
                  className="cursor-pointer border-b border-ink/5 transition hover:bg-emerald-500/[0.08] dark:border-white/5 dark:hover:bg-emerald-500/10"
                >
                  <td className="py-2 pr-4 font-medium text-ink dark:text-mist">{t.tenant_nome}</td>
                  <td className="py-2 pr-4 font-mono text-xs text-ink/55 dark:text-frost-muted">{t.slug}</td>
                  <td className="py-2 pr-4 text-right tabular-nums text-ink dark:text-mist">{t.orders_cnt}</td>
                  <td className="py-2 pr-4 text-right tabular-nums text-ink dark:text-mist">{formatBrl(t.gmv)}</td>
                  <td className="py-2 text-right tabular-nums font-semibold text-emerald-800 dark:text-emerald-200">
                    {formatBrl(t.revenue_ok)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!summary?.by_tenant || summary.by_tenant.length === 0) && (
            <p className="mt-4 text-sm text-ink/50 dark:text-frost-muted">Sem pedidos no período.</p>
          )}
        </div>
      </div>

      <div
        id="fin-mod-orders"
        className="scroll-mt-24 space-y-4 rounded-3xl border border-ink/10 bg-white/70 p-6 dark:border-white/10 dark:bg-white/[0.04]"
      >
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h4 className="font-display text-lg font-semibold text-ink dark:text-mist">Pedidos (todas as lojas)</h4>
            <p className="mt-1 text-xs text-ink/50 dark:text-frost-muted">
              Filtros herdam o período acima. Ajuste de estado é auditado (suporte / conciliação).
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={exportCsv}
              className="rounded-xl border border-ink/15 bg-white px-4 py-2 text-xs font-bold uppercase text-ink dark:border-white/15 dark:bg-[#0f1815] dark:text-frost"
            >
              CSV (página)
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <select
            value={tenantFilterOrders}
            onChange={(e) => {
              setTenantFilterOrders(e.target.value);
              setOrderOffset(0);
            }}
            className="rounded-xl border border-ink/15 bg-white px-3 py-2 text-sm text-ink dark:border-white/10 dark:bg-[#0f1815] dark:text-frost"
          >
            <option value="">Todas empresas</option>
            {tenants.map((t) => (
              <option key={t.id} value={String(t.id)}>
                #{t.id} {t.nome}
              </option>
            ))}
          </select>
          <select
            value={statusFilterOrders}
            onChange={(e) => {
              setStatusFilterOrders(e.target.value);
              setOrderOffset(0);
            }}
            className="rounded-xl border border-ink/15 bg-white px-3 py-2 text-sm text-ink dark:border-white/10 dark:bg-[#0f1815] dark:text-frost"
          >
            <option value="">Todos os status</option>
            {STATUS_OPTS.map((s) => (
              <option key={s} value={s}>
                {orderStatusLabelPtBr(s)}
              </option>
            ))}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] border-collapse text-left text-xs">
            <thead>
              <tr className="border-b border-ink/15 bg-zinc-100/95 text-[11px] font-bold uppercase tracking-wide text-ink dark:border-white/15 dark:bg-zinc-900/85 dark:text-mist">
                <th className="px-3 py-2.5 pr-2">Id</th>
                <th className="px-3 py-2.5 pr-2">Empresa</th>
                <th className="px-3 py-2.5 pr-2">Status</th>
                <th className="px-3 py-2.5 pr-2 text-right">Total</th>
                <th className="px-3 py-2.5 pr-2">Cupom</th>
                <th className="px-3 py-2.5 pr-2">Mercado Pago</th>
                <th className="px-3 py-2.5 pr-2">Cliente</th>
                <th className="px-3 py-2.5 pr-2">Data</th>
                <th className="px-3 py-2.5 pr-2">Detalhe</th>
                <th className="px-3 py-2.5">Ações</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={`${o.tenant_id}-${o.id}`} className="border-b border-ink/5 dark:border-white/5">
                  <td className="py-2 pr-2 font-mono tabular-nums">{o.id}</td>
                  <td className="py-2 pr-2 max-w-[10rem] truncate" title={o.tenant_nome}>
                    {o.tenant_nome}
                  </td>
                  <td className="py-2 pr-2">{orderStatusLabelPtBr(o.status)}</td>
                  <td className="py-2 pr-2 text-right tabular-nums font-semibold">{formatBrl(o.total)}</td>
                  <td className="py-2 pr-2 font-mono text-[10px]">{o.coupon_code || '—'}</td>
                  <td className="py-2 pr-2 font-mono text-[10px] text-ink/60 dark:text-frost-muted">
                    {o.mp_payment_id ? String(o.mp_payment_id).slice(0, 12) + '…' : o.mp_preference_id ? 'pref…' : '—'}
                  </td>
                  <td className="py-2 pr-2 max-w-[8rem] truncate">{o.customer_nome}</td>
                  <td className="py-2 pr-2 whitespace-nowrap text-ink/75 dark:text-frost">{o.created_at}</td>
                  <td className="py-2 pr-2">
                    <button
                      type="button"
                      onClick={() =>
                        setOrderDrawer({ tenant_id: Number(o.tenant_id), order_id: Number(o.id) })
                      }
                      className="text-[10px] font-bold uppercase text-sky-700 underline dark:text-sky-300"
                    >
                      Ver
                    </button>
                  </td>
                  <td className="py-2">
                    <button
                      type="button"
                      onClick={() => {
                        const cur = typeof o.status === 'string' ? o.status.trim() : '';
                        setStatusPick(STATUS_OPTS.includes(cur) ? cur : 'pending');
                        setStatusModal({ order: o });
                      }}
                      className="text-[10px] font-bold uppercase text-emerald-700 underline dark:text-emerald-300"
                    >
                      estado
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
          <span className="tabular-nums text-ink/55 dark:text-frost-muted">
            {ordersTotal === 0 ? '0 linhas' : `${orderOffset + 1}–${Math.min(orderOffset + orderLimit, ordersTotal)} de ${ordersTotal}`}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={orderOffset <= 0}
              onClick={() => setOrderOffset((x) => Math.max(0, x - orderLimit))}
              className="rounded-lg border border-ink/15 px-3 py-1 font-bold uppercase disabled:opacity-40 dark:border-white/15"
            >
              Anterior
            </button>
            <button
              type="button"
              disabled={orderOffset + orderLimit >= ordersTotal}
              onClick={() => setOrderOffset((x) => x + orderLimit)}
              className="rounded-lg border border-ink/15 px-3 py-1 font-bold uppercase disabled:opacity-40 dark:border-white/15"
            >
              Seguinte
            </button>
          </div>
        </div>
      </div>

      <div
        id="fin-mod-coupons"
        className="scroll-mt-24 space-y-6 rounded-3xl border border-ink/15 bg-white/80 p-5 shadow-sm dark:border-white/12 dark:bg-white/[0.06] sm:p-7 md:p-8"
      >
        <div>
          <h4 className="font-display text-lg font-semibold text-ink dark:text-mist">Gestão corporativa de cupons</h4>
          <p className="mt-1 max-w-4xl text-xs leading-relaxed text-ink/65 dark:text-frost-muted">
            Código único por loja · uso único (uma compra) ou campanha (data + repetir; limite opcional de usos) · remoção só
            sem consumo registado conforme tipo.
          </p>
        </div>
        <form
          onSubmit={submitCoupon}
          className="grid grid-cols-1 gap-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.07] p-4 dark:border-emerald-500/25 dark:bg-emerald-950/25 sm:p-5 md:grid-cols-2 md:items-end lg:grid-cols-12 lg:gap-x-4 lg:gap-y-4"
        >
          <label className="min-w-0 text-[11px] font-bold uppercase tracking-wide text-ink/70 dark:text-frost lg:col-span-4">
            Empresa
            <select
              required
              value={newCupTenantId}
              onChange={(e) => setNewCupTenantId(e.target.value)}
              disabled={cupBusy}
              className="mt-1.5 block w-full min-w-0 rounded-xl border border-ink/20 bg-white px-3 py-2.5 text-sm text-ink shadow-sm dark:border-white/15 dark:bg-[#0f1815] dark:text-mist"
            >
              <option value="">— Escolher loja —</option>
              {tenants.map((t) => (
                <option key={t.id} value={String(t.id)}>
                  #{t.id} {t.nome}
                </option>
              ))}
            </select>
          </label>
          <label className="min-w-0 text-[11px] font-bold uppercase tracking-wide text-ink/70 dark:text-frost lg:col-span-3">
            Código
            <input
              required
              value={newCupCode}
              onChange={(e) => setNewCupCode(e.target.value.toUpperCase())}
              disabled={cupBusy}
              className="mt-1.5 block w-full max-w-full rounded-xl border border-ink/20 bg-white px-3 py-2.5 font-mono text-sm text-ink shadow-sm dark:border-white/15 dark:bg-[#0f1815] dark:text-mist sm:max-w-md"
            />
          </label>
          <label className="min-w-0 text-[11px] font-bold uppercase tracking-wide text-ink/70 dark:text-frost lg:col-span-2">
            % 0–100
            <input
              required
              type="number"
              min={0}
              max={100}
              step={0.01}
              value={newCupPct}
              onChange={(e) => setNewCupPct(e.target.value)}
              disabled={cupBusy}
              className="mt-1.5 block w-full max-w-[8rem] rounded-xl border border-ink/20 bg-white px-3 py-2.5 text-sm text-ink shadow-sm dark:border-white/15 dark:bg-[#0f1815] dark:text-mist"
            />
          </label>
          <fieldset className="flex flex-wrap items-center gap-4 rounded-xl border border-ink/15 bg-white/60 px-3 py-3 dark:border-white/12 dark:bg-[#0f1815]/60 lg:col-span-3">
            <legend className="sr-only">Tipo</legend>
            <span className="w-full text-[10px] font-bold uppercase tracking-wide text-ink/55 dark:text-frost-muted lg:w-auto">
              Tipo
            </span>
            <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-ink dark:text-mist">
              <input
                type="radio"
                checked={newCupKind === 'single'}
                disabled={cupBusy}
                onChange={() => setNewCupKind('single')}
              />
              Único
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-ink dark:text-mist">
              <input
                type="radio"
                checked={newCupKind === 'multi'}
                disabled={cupBusy}
                onChange={() => setNewCupKind('multi')}
              />
              Campanha
            </label>
          </fieldset>
          {newCupKind === 'multi' ? (
            <>
              <label className="min-w-0 text-[11px] font-bold uppercase tracking-wide text-ink/70 dark:text-frost md:col-span-1 lg:col-span-3">
                Válido até
                <input
                  required
                  type="date"
                  value={newCupValidUntil}
                  onChange={(e) => setNewCupValidUntil(e.target.value)}
                  disabled={cupBusy}
                  className="mt-1.5 block w-full rounded-xl border border-ink/20 bg-white px-3 py-2.5 text-sm text-ink shadow-sm dark:border-white/15 dark:bg-[#0f1815] dark:text-mist"
                />
              </label>
              <label className="min-w-0 text-[11px] font-bold uppercase tracking-wide text-ink/70 dark:text-frost md:col-span-1 lg:col-span-3">
                Usos máx. (opcional)
                <input
                  type="number"
                  min={1}
                  placeholder="∞"
                  value={newCupMaxUses}
                  onChange={(e) => setNewCupMaxUses(e.target.value)}
                  disabled={cupBusy}
                  className="mt-1.5 block w-full max-w-[12rem] rounded-xl border border-ink/20 bg-white px-3 py-2.5 text-sm text-ink shadow-sm dark:border-white/15 dark:bg-[#0f1815] dark:text-mist"
                />
              </label>
            </>
          ) : null}
          <div className="flex md:col-span-2 lg:col-span-12 lg:justify-end">
            <button
              type="submit"
              disabled={cupBusy}
              className="w-full rounded-xl bg-ink px-6 py-3 text-sm font-bold text-mist shadow-sm transition hover:opacity-95 disabled:opacity-50 dark:bg-mist dark:text-ink sm:w-auto sm:px-8"
            >
              Criar cupom
            </button>
          </div>
        </form>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <select
            value={couponTenant}
            onChange={(e) => setCouponTenant(e.target.value)}
            className="w-full min-w-0 rounded-xl border border-ink/20 bg-white px-3 py-2.5 text-sm text-ink shadow-sm dark:border-white/15 dark:bg-[#0f1815] dark:text-mist sm:max-w-md sm:flex-1"
          >
            <option value="">Todas lojas</option>
            {tenants.map((t) => (
              <option key={t.id} value={String(t.id)}>
                #{t.id} {t.nome}
              </option>
            ))}
          </select>
          <label className="flex cursor-pointer items-center gap-2.5 text-xs font-bold uppercase tracking-wide text-ink/80 dark:text-frost">
            <input
              type="checkbox"
              checked={couponUnusedOnly}
              onChange={(e) => setCouponUnusedOnly(e.target.checked)}
              className="h-4 w-4 rounded border-ink/30 text-emerald-600 focus:ring-emerald-500/40 dark:border-white/25"
            />
            Só disponíveis
          </label>
        </div>
        <div className="overflow-x-auto rounded-xl border border-ink/12 shadow-sm dark:border-white/10">
          <table className="w-full min-w-[960px] border-collapse text-left text-xs">
            <thead>
              <tr className="border-b border-ink/15 bg-zinc-100/95 text-[11px] font-bold uppercase tracking-wide text-ink dark:border-white/15 dark:bg-zinc-900/85 dark:text-mist">
                <th className="px-3 py-2.5 pr-2">Cupom</th>
                <th className="px-3 py-2.5 pr-2">Loja</th>
                <th className="px-3 py-2.5 pr-2 text-right">%</th>
                <th className="px-3 py-2.5 pr-2">Tipo</th>
                <th className="px-3 py-2.5 pr-2 max-w-[9rem]">Campanha</th>
                <th className="px-3 py-2.5 pr-2">Criado</th>
                <th className="px-3 py-2.5 pr-2">Estado</th>
                <th className="px-3 py-2.5 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => {
                const kindStr = typeof c.kind === 'string' ? c.kind : '';
                const isMulti = kindStr === 'multi';
                const uses = Number(c.uses_count) || 0;
                const maxU = c.max_uses != null && c.max_uses !== '' ? Number(c.max_uses) : null;
                const canDelete =
                  (isMulti ? uses === 0 : !c.used_at);
                const campTxt = isMulti
                  ? `${formatYmdPtBr(c.valid_until)} · ${uses}${
                      maxU != null && Number.isFinite(maxU) ? `/${maxU}` : '/∞'
                    }`
                  : '—';

                return (
                  <tr key={c.id} className="border-b border-ink/10 bg-white/40 dark:border-white/10 dark:bg-white/[0.02]">
                    <td className="px-3 py-2.5 pr-2 font-mono font-bold text-ink dark:text-mist">{c.code}</td>
                    <td className="px-3 py-2.5 pr-2 max-w-[12rem] truncate text-ink dark:text-mist" title={c.tenant_nome}>
                      {c.tenant_nome}
                    </td>
                    <td className="px-3 py-2.5 pr-2 text-right tabular-nums text-ink dark:text-mist">
                      {Number(c.percent_discount)}%
                    </td>
                    <td className="px-3 py-2.5 pr-2 text-ink/90 dark:text-frost">{isMulti ? 'campanha' : 'único'}</td>
                    <td
                      className="px-3 py-2.5 pr-2 truncate text-[11px] leading-tight text-ink/80 dark:text-mist"
                      title={campTxt}
                    >
                      {campTxt}
                    </td>
                    <td className="px-3 py-2.5 pr-2 whitespace-nowrap text-ink/75 dark:text-frost">{c.created_at}</td>
                    <td className="px-3 py-2.5 pr-2">
                      {isMulti ? (
                        <span className="tabular-nums text-[10px] font-semibold text-ink/80 dark:text-mist">
                          {uses}
                          {maxU != null && Number.isFinite(maxU) ? `/${maxU}` : ''} uso(s)
                          {isMulti && uses === 0 && !c.used_at ? (
                            <span className="ml-2 rounded-full bg-sky-500/20 px-1.5 py-0.5 text-[9px] font-bold uppercase text-sky-900 dark:text-sky-100">
                              novo
                            </span>
                          ) : null}
                        </span>
                      ) : c.used_at ? (
                        <span className="rounded-full bg-slate-500/20 px-2 py-0.5 text-[10px] font-bold uppercase dark:bg-slate-500/30">
                          usado #{c.used_order_id}
                        </span>
                      ) : (
                        <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-900 dark:bg-emerald-400/25 dark:text-emerald-50">
                          livre
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {canDelete ? (
                        <button
                          type="button"
                          disabled={cupBusy}
                          onClick={() => removeCoupon(c.id, c.tenant_id, ` «${String(c.code)}»`)}
                          className="text-[11px] font-bold uppercase text-red-600 underline decoration-red-600/50 underline-offset-2 hover:text-red-700 dark:text-red-400 dark:decoration-red-400/50"
                        >
                          apagar
                        </button>
                      ) : (
                        <span className="text-[11px] font-medium tabular-nums text-ink/50 dark:text-frost-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {orderDrawer ? (
        <div
          className="fixed inset-0 z-[60] flex justify-end bg-black/50 backdrop-blur-sm"
          role="presentation"
          onClick={() => {
            if (!orderDrawerBusy) setOrderDrawer(null);
          }}
        >
          <aside
            role="dialog"
            aria-modal="true"
            aria-labelledby="fin-order-drawer-title"
            aria-busy={orderDrawerBusy}
            className="flex h-full w-full max-w-lg flex-col border-l border-ink/10 bg-white shadow-2xl dark:border-white/10 dark:bg-[#0c1210]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-ink/10 px-5 py-4 dark:border-white/10">
              <div>
                <h5 id="fin-order-drawer-title" className="font-display text-lg font-semibold text-ink dark:text-mist">
                  Pedido #{orderDrawer.order_id}
                </h5>
                <p className="mt-1 font-mono text-[11px] text-ink/55 dark:text-frost-muted">
                  tenant_id {orderDrawer.tenant_id}
                </p>
              </div>
              <button
                type="button"
                disabled={orderDrawerBusy}
                onClick={() => setOrderDrawer(null)}
                className="rounded-xl border border-ink/15 px-3 py-1.5 text-xs font-bold uppercase text-ink/80 disabled:opacity-50 dark:border-white/20 dark:text-frost"
              >
                Fechar
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              {orderDrawerBusy ? (
                <p className="text-sm text-ink/55 dark:text-frost-muted">A carregar detalhe completo…</p>
              ) : orderDrawerErr ? (
                <p className="text-sm font-semibold text-red-600 dark:text-red-400">{orderDrawerErr}</p>
              ) : orderDrawerData ? (
                <div className="space-y-4 text-sm">
                  <div>
                    <p className="text-[10px] font-bold uppercase text-ink/50 dark:text-frost-muted">Loja</p>
                    <p className="font-semibold text-ink dark:text-mist">{orderDrawerData.tenant_nome}</p>
                    <p className="font-mono text-xs text-ink/55 dark:text-frost-muted">/{orderDrawerData.tenant_slug}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] font-bold uppercase text-ink/50 dark:text-frost-muted">Estado</p>
                      <p className="font-semibold text-ink dark:text-mist">{orderStatusLabelPtBr(String(orderDrawerData.status || ''))}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase text-ink/50 dark:text-frost-muted">Total</p>
                      <p className="font-display text-lg font-semibold tabular-nums text-ink dark:text-mist">{formatBrl(orderDrawerData.total)}</p>
                    </div>
                  </div>
                  {(orderDrawerData.subtotal_before_discount != null ||
                    orderDrawerData.discount_percent != null) && (
                    <div className="rounded-xl border border-ink/10 bg-black/[0.03] px-3 py-2 text-xs dark:border-white/10 dark:bg-white/[0.04]">
                      <p className="text-ink/60 dark:text-frost-muted">
                        Subtotal antes desconto:{' '}
                        <span className="font-mono font-semibold text-ink dark:text-mist">
                          {formatBrl(orderDrawerData.subtotal_before_discount)}
                        </span>
                        {orderDrawerData.discount_percent != null ? (
                          <>
                            {' '}
                            · desconto {orderDrawerData.discount_percent}%
                          </>
                        ) : null}
                      </p>
                      {orderDrawerData.coupon_code ? (
                        <p className="mt-1 font-mono text-[11px] text-ink/70 dark:text-frost-muted">
                          Cupom: {orderDrawerData.coupon_code}
                        </p>
                      ) : null}
                    </div>
                  )}
                  <div>
                    <p className="text-[10px] font-bold uppercase text-ink/50 dark:text-frost-muted">Cliente</p>
                    <p className="font-medium text-ink dark:text-mist">{orderDrawerData.customer_nome}</p>
                    <p className="text-xs text-ink/65 dark:text-frost-muted">{orderDrawerData.customer_email}</p>
                    <p className="text-xs text-ink/65 dark:text-frost-muted">{orderDrawerData.customer_telefone || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase text-ink/50 dark:text-frost-muted">Mercado Pago</p>
                    <dl className="mt-1 space-y-1 font-mono text-[11px] text-ink/75 dark:text-frost">
                      <div>
                        <dt className="inline text-ink/50 dark:text-frost-muted">preference </dt>
                        <dd className="inline break-all">{orderDrawerData.mp_preference_id || '—'}</dd>
                      </div>
                      <div>
                        <dt className="inline text-ink/50 dark:text-frost-muted">payment </dt>
                        <dd className="inline break-all">{orderDrawerData.mp_payment_id || '—'}</dd>
                      </div>
                      <div>
                        <dt className="inline text-ink/50 dark:text-frost-muted">status </dt>
                        <dd className="inline">{orderDrawerData.mp_payment_status || '—'}</dd>
                      </div>
                    </dl>
                  </div>
                  {orderDrawerData.payment_method_code ? (
                    <p className="text-xs">
                      <span className="font-bold text-ink/50 dark:text-frost-muted">Pagamento: </span>
                      {orderDrawerData.payment_method_code}
                    </p>
                  ) : null}
                  {(orderDrawerData.entrega_numero || orderDrawerData.entrega_complemento) && (
                    <p className="text-xs text-ink/70 dark:text-frost-muted">
                      Entrega: {orderDrawerData.entrega_numero} {orderDrawerData.entrega_complemento}
                    </p>
                  )}
                  {orderDrawerData.observacoes ? (
                    <div>
                      <p className="text-[10px] font-bold uppercase text-ink/50 dark:text-frost-muted">Observações</p>
                      <p className="whitespace-pre-wrap text-xs leading-relaxed text-ink/80 dark:text-mist">
                        {orderDrawerData.observacoes}
                      </p>
                    </div>
                  ) : null}
                  <div className="text-[11px] text-ink/50 dark:text-frost-muted">
                    <p>Criado: {String(orderDrawerData.created_at || '')}</p>
                    <p>Actualizado: {String(orderDrawerData.updated_at || '')}</p>
                  </div>
                  <div>
                    <p className="mb-2 text-[10px] font-bold uppercase text-ink/50 dark:text-frost-muted">Itens</p>
                    <FinanceOrderItemsTable items={orderDrawerData.items} />
                  </div>
                </div>
              ) : (
                <p className="text-sm text-ink/50 dark:text-frost-muted">Sem dados.</p>
              )}
            </div>
            <div className="shrink-0 border-t border-ink/10 bg-white/95 px-5 py-4 dark:border-white/10 dark:bg-[#0c1210]">
              <button
                type="button"
                disabled={!orderDrawerData || orderDrawerBusy}
                onClick={openStatusFromOrderDrawer}
                className="w-full rounded-2xl bg-emerald-600 px-4 py-3 text-xs font-bold uppercase text-white disabled:opacity-40"
              >
                Alterar estado do pedido…
              </button>
              <p className="mt-2 text-center text-[10px] text-ink/50 dark:text-frost-muted">
                Esc para fechar · alterações são auditadas
              </p>
            </div>
          </aside>
        </div>
      ) : null}

      {statusModal?.order ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="max-w-md rounded-3xl border border-ink/10 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-[#101820]">
            <h5 className="font-display text-lg font-semibold">Atualizar status do pedido</h5>
            <p className="mt-2 text-xs text-ink/55 dark:text-frost-muted">
              Pedido #{/** @type{any} */ (statusModal.order).id} ·{' '}
              {/** @type{any} */ (statusModal.order).tenant_nome}
            </p>
            <select
              value={statusPick}
              onChange={(e) => setStatusPick(e.target.value)}
              className="mt-4 w-full rounded-xl border border-ink/15 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#0f1815]"
            >
              {STATUS_OPTS.map((s) => (
                <option key={s} value={s}>
                  {orderStatusLabelPtBr(s)}
                </option>
              ))}
            </select>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setStatusModal(null)}
                className="rounded-xl border border-ink/15 px-4 py-2 text-xs font-bold uppercase dark:border-white/15"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={statusSaving}
                onClick={saveOrderStatus}
                className="rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold uppercase text-white"
              >
                Gravar
              </button>
            </div>
          </div>
        </div>
      ) : null}
        </>
      ) : null}
    </div>
  );
}
